import { memo, useMemo } from 'react';
import { MapEvent, MapMouseEvent } from 'mapbox-gl';
import { MapRef } from 'react-map-gl';

import { ParcellesFieldPopup, ParcellesFieldPopupData } from '../../components/parcelles-field-popup/parcelles-field-popup';
import { ContractIlotFieldLayer } from '../../lib/types/contract-ilot-fields';

import {
  ContractParcellesLayer,
  parcellesFillLayerId,
  parcellesOutlineLayerId,
  PfMap,
} from '@map';

interface LayerVisualProps {
  fillOpacity: number;
  lineOpacity: number;
  lineWidth: number;
}

interface ContractParcellesMapProps {
  filteredLayers: ContractIlotFieldLayer[];
  focusedCampaignId: number | null;
  popupData: ParcellesFieldPopupData | null;
  mapRef: React.Ref<MapRef>;
  onLoad: (event: MapEvent) => void;
  onMapClick: (event: MapMouseEvent) => void;
  onClosePopup: () => void;
}

const FILL_OPACITY = 0.4;

function getLayerVisualProps(
  campaignId: number,
  focusedCampaignId: number | null,
): LayerVisualProps {
  if (focusedCampaignId == null) {
    return {
      fillOpacity: FILL_OPACITY,
      lineOpacity: 0.9,
      lineWidth: 3,
    };
  }
  if (focusedCampaignId === campaignId) {
    return {
      fillOpacity: FILL_OPACITY,
      lineOpacity: 1,
      lineWidth: 4,
    };
  }
  return {
    fillOpacity: 0.04,
    lineOpacity: 0.45,
    lineWidth: 2,
  };
}

export const ContractParcellesMap = memo(function ContractParcellesMap({
  filteredLayers,
  focusedCampaignId,
  popupData,
  mapRef,
  onLoad,
  onMapClick,
  onClosePopup,
}: ContractParcellesMapProps) {
  const interactiveLayerIds = useMemo(
    () =>
      filteredLayers.flatMap((layer) => [
        parcellesFillLayerId(layer.campaign_id),
        parcellesOutlineLayerId(layer.campaign_id),
      ]),
    [filteredLayers],
  );

  return (
    <PfMap
      interactiveLayerIds={interactiveLayerIds}
      onClick={onMapClick}
      onLoad={onLoad}
      ref={mapRef}
    >
      {filteredLayers.map((layer, index) => {
        const visual = getLayerVisualProps(layer.campaign_id, focusedCampaignId);
        return (
          <ContractParcellesLayer
            campaignId={layer.campaign_id}
            color={layer.color}
            dashPatternIndex={index}
            data={layer.fields}
            fillOpacity={visual.fillOpacity}
            key={layer.campaign_id}
            lineOpacity={visual.lineOpacity}
            lineWidth={visual.lineWidth}
          />
        );
      })}
      <ParcellesFieldPopup data={popupData} onClose={onClosePopup} />
    </PfMap>
  );
});
